import { Box, CircularProgress, Typography } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { CrmBoardBar } from './components/toolbar/crm-board-bar';
import { CrmBoardColumns } from './components/cards/crm-board-columns';
import { CrmBoardPicker } from './components/toolbar/crm-board-picker';
import { CrmAddLeadModal } from './components/modals/crm-add-lead-modal';
import { CrmColumnModal } from './components/modals/crm-column-modal';
import { CrmBoardModal } from './components/modals/crm-board-modal';
import { CrmWonValueModal } from './components/modals/crm-won-value-modal';
import { CrmLossReasonModal } from './components/modals/crm-loss-reason-modal';
import { CrmMoveBoardModal } from './components/modals/crm-move-board-modal';
import { CrmLeadList } from './components/list/crm-lead-list';
import { DropAction } from './drag/crm-drag-data';
import { useCrmBoards } from './hooks/use-crm-boards.hook';
import { useCrmKanban } from './hooks/use-crm-kanban.hook';
import { useLeadStatus } from './hooks/use-lead-status.hook';
import { useCrmUsers } from './hooks/use-crm-users.hook';
import { useTags } from '@layouts/pages/Settings/general/tags/hooks/use-tags.hook';
import { useCustomFields } from '@layouts/pages/Settings/general/custom-fields/hooks/use-custom-fields.hook';
import { useCrmDrag } from './hooks/use-crm-drag.hook';
import { useCrmBoardActions } from './hooks/use-crm-board-actions.hook';
import { useAvailableHeight } from '../hooks/use-available-height.hook';
import { BoardColumnWithLeads } from './services/responses/board-column.response';
import { BoardSummaryResponse } from './services/responses/board.response';
import { ELeadOutcome, LeadResponse } from './services/responses/lead.response';
import { CrmLeadService } from './services/crm-lead.service';
import { CrmLeadListService } from './services/crm-lead-list.service';
import { buildLeadListQuery } from './filters/crm-lead-list-query';
import { leadExportRows } from './lead-export';
import { datedFileName, downloadSpreadsheet } from '@functions/export-spreadsheet';
import { CrmColumnService } from './services/crm-column.service';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from './crm-theme';
import { ScheduleFilter } from './filters/crm-schedule-filter';
import { CrmKanbanView, readKanbanView, saveKanbanView } from './crm-kanban-view';
import { OUTCOME_SAVED } from './formatters/crm-outcome-message';

/// Teto da planilha: passar disso indica filtro esquecido, e a tela avisa.
const EXPORT_LIMIT = 5000;

export const CrmKanban = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const [exporting, setExporting] = useState(false);
    const navigate = useNavigate();

    const { summaries, refetch: refetchBoards } = useCrmBoards();
    const {
        board, loading, loadBoard, refreshBoard,
        applyOptimisticColumnReorder, applyOptimisticLeadMove,
        reorderColumns, moveLead, reorderLeads, setBoard,
        showClosed, setShowClosed,
    } = useCrmKanban();
    const { statuses } = useLeadStatus();
    const { users } = useCrmUsers();
    const { tags } = useTags();
    const { fields: customFields } = useCustomFields();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedBoardId, setSelectedBoardId] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilterId, setStatusFilterId] = useState<string | null>(null);
    const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilter | null>(null);
    /// Id do responsável, OWNER_UNASSIGNED ou nulo para todos.
    const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
    /// Id da tag ou nulo para todas.
    const [tagFilter, setTagFilter] = useState<string | null>(null);

    /// Pedido do painel ao abrir o quadro (?responsavel, ?quadro, ?coluna); trocar de quadro zera.
    const linkRef = useRef({
        responsavel: searchParams.get('responsavel'),
        quadro: searchParams.get('quadro'),
        coluna: searchParams.get('coluna'),
    });

    /// Coluna que o painel pediu para mostrar; some sozinha, como o card do Multicanal.
    const [focusedColumn, setFocusedColumn] = useState<string | null>(null);
    const [view, setView] = useState<CrmKanbanView>(readKanbanView);

    /// Sinal para a lista buscar de novo: ela pagina no servidor e não enxerga o quadro.
    const [listRefresh, setListRefresh] = useState(false);

    /// Card que o Multicanal pediu para mostrar; sai sozinho, é dica visual e não estado do lead.
    const [focusedLead, setFocusedLead] = useState<string | null>(null);
    const [addLeadColumnId, setAddLeadColumnId] = useState<string | null>(null);
    const [creatingBoardOpen, setCreatingBoardOpen] = useState(false);
    const [editBoard, setEditBoard] = useState<BoardSummaryResponse | null>(null);
    const [boardMenuAnchor, setBoardMenuAnchor] = useState<null | HTMLElement>(null);
    const [editColumn, setEditColumn] = useState<BoardColumnWithLeads | null>(null);
    const [addingColumn, setAddingColumn] = useState(false);

    /// Lead solto numa zona do rodapé, esperando o que falta para gravar.
    const [dropped, setDropped] = useState<{ action: DropAction; lead: LeadResponse } | null>(null);
    const boardContainerRef = useRef<HTMLDivElement | null>(null);

    /// Abaixo das colunas vêm 16+16+24px de respiro; com a folga padrão de 28, a página rolava.
    const { topRef: boardBarRef, targetRef: columnsRowRef, height: columnsMaxHeight, scrollbarHeight } =
        useAvailableHeight(`${board?.id}:${board?.columns.length}:${view}`, { bottomGap: 56 });

    const drag = useCrmDrag({
        board,
        applyOptimisticColumnReorder,
        applyOptimisticLeadMove,
        reorderColumns,
        moveLead,
        reorderLeads,
        refreshBoard,
        onError: (message) => showAlert(message, 'error', 3000),
        onDropAction: (action, lead) => setDropped({ action, lead }),
    });

    const { handleDeleteColumn, handleDeleteBoard, handleToggleBoardActive } = useCrmBoardActions({
        board,
        summaries,
        selectedBoardId,
        setSelectedBoardId,
        setBoard,
        refreshBoard,
        refetchBoards,
    });

    useEffect(() => {
        if (summaries.length > 0 && !selectedBoardId) {
            const linked = summaries.find((s) => s.id === linkRef.current.quadro);
            const defaultBoard = summaries.find((s) => s.isDefault);
            setSelectedBoardId(linked?.id ?? defaultBoard?.id ?? summaries[0].id);
        }
    }, [summaries]);

    useEffect(() => {
        if (selectedBoardId) {
            setStatusFilterId(null);
            setScheduleFilter(null);
            setTagFilter(null);
            // O responsável do painel vale só para o primeiro quadro aberto.
            setOwnerFilter(linkRef.current.responsavel);
            linkRef.current.responsavel = null;
            loadBoard(selectedBoardId);
            boardContainerRef.current?.scrollTo(0, 0);
            window.scrollTo(0, 0);
        }
    }, [selectedBoardId]);

    // O pedido do painel já foi guardado: sai da URL para não voltar num F5.
    useEffect(() => {
        const link = ['responsavel', 'quadro', 'coluna'].filter((param) => searchParams.has(param));

        if (link.length === 0) return;

        link.forEach((param) => searchParams.delete(param));
        setSearchParams(searchParams, { replace: true });
    }, []);

    // A coluna pedida pelo painel: rola até ela quando o quadro pinta.
    useEffect(() => {
        const columnId = linkRef.current.coluna;

        if (!columnId || !board?.columns.some((column) => column.id === columnId)) return;

        linkRef.current.coluna = null;
        setFocusedColumn(columnId);

        requestAnimationFrame(() => {
            document.getElementById(`board-column-${columnId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        });
    }, [board]);

    useEffect(() => {
        if (!focusedColumn) return;

        const timer = setTimeout(() => setFocusedColumn(null), 4000);

        return () => clearTimeout(timer);
    }, [focusedColumn]);

    // A busca global manda ?lead=<id>; o lead tem tela própria, então o quadro só encaminha.
    useEffect(() => {
        const leadDaUrl = searchParams.get('lead');

        if (!leadDaUrl) return;

        searchParams.delete('lead');
        setSearchParams(searchParams, { replace: true });
        navigate(`/crm/lead/${leadDaUrl}`);
    }, []);

    // O Multicanal manda ?focoLead=<id> (a busca usa ?lead=): o card é mostrado no quadro, sem modal.
    useEffect(() => {
        const focusId = searchParams.get('focoLead');

        if (!focusId || !board) return;

        const exists = board.columns
            .flatMap((column) => column.leads)
            .some((lead) => lead.id === focusId);

        // Encerrado ou de outro quadro: abre a tela do lead, que sempre existe, em vez de avisar.
        if (!exists) {
            navigate(`/crm/lead/${focusId}`);
            return;
        }

        setFocusedLead(focusId);

        // Espera o quadro pintar: sem isso o card não existe no DOM e o scroll não tem destino.
        requestAnimationFrame(() => {
            document.getElementById(`lead-card-${focusId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            });
        });

        searchParams.delete('focoLead');
        setSearchParams(searchParams, { replace: true });
    }, [board]);

    // O destaque é dica visual, não estado do lead: some sozinho.
    useEffect(() => {
        if (!focusedLead) return;

        const timer = setTimeout(() => setFocusedLead(null), 4000);

        return () => clearTimeout(timer);
    }, [focusedLead]);

    const refreshCurrentBoard = async () => {
        setListRefresh((current) => !current);
        if (selectedBoardId) await refreshBoard(selectedBoardId);
    };

    const changeView = (next: CrmKanbanView) => {
        setView(next);
        saveKanbanView(next);
    };

    const handleChangeLeadStatus = async (lead: LeadResponse, statusId: string) => {
        try {
            await new CrmLeadService().changeStatus(lead.id, statusId);
            showAlert(t('general.atualizacao'), 'success', 2000);
            await refreshCurrentBoard();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        }
    };

    const handleSetEntryColumn = async (columnId: string) => {
        try {
            await new CrmColumnService().setEntry(columnId);
            showAlert(t('crmKanban.entradaDefinida'), 'success', 2500);
            await refreshCurrentBoard();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        }
    };

    const markOutcome = async (
        lead: LeadResponse,
        outcome: ELeadOutcome,
        extra: { lossReasonId?: string | null; lossNote?: string | null; closedValue?: number | null }
    ) => {
        setDropped(null);

        try {
            await new CrmLeadService().setOutcome(lead.id, { outcome, ...extra });
            showAlert(t(OUTCOME_SAVED[outcome]), 'success', 2000);
            await refreshCurrentBoard();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        }
    };

    const canMoveToBoard = summaries.some((s) => s.active && s.id !== selectedBoardId);

    const handleAssignLead = async (lead: LeadResponse, userId: string | null) => {
        try {
            await new CrmLeadService().assign(lead.id, userId);
            await refreshCurrentBoard();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        }
    };

    /// Exporta o que os filtros mostram nas duas visões, não só a página da lista.
    const exportLeads = async () => {
        if (!board) return;

        setExporting(true);

        try {
            const query = buildLeadListQuery({
                boardId: board.id,
                search: searchTerm,
                statusId: statusFilterId,
                ownerFilter,
                tagFilter,
                includeClosed: showClosed,
                scheduleFilter,
                now: new Date(),
            });

            const res = await new CrmLeadListService().getAll(query, {
                currentPage: 1,
                recordsPerPage: EXPORT_LIMIT,
            });

            const leads = res.data.data?.records ?? [];
            const stageNames = new Map(board.columns.map((column) => [column.id, column.name]));

            downloadSpreadsheet(datedFileName('leads'), [
                { name: board.name, rows: leadExportRows(leads, stageNames, t, customFields) },
            ]);

            if ((res.data.data?.totalItens ?? 0) > EXPORT_LIMIT)
                showAlert(t('exportar.limite', { total: EXPORT_LIMIT }), 'warning', 5000);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setExporting(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('crmKanban.titulo')} />

            <Box ref={boardContainerRef} sx={{ background: cc.pageBg, borderRadius: 3, p: 2 }}>
                <CrmBoardBar
                    barRef={boardBarRef}
                    board={board}
                    statuses={statuses}
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    statusFilterId={statusFilterId}
                    onStatusFilterChange={setStatusFilterId}
                    scheduleFilter={scheduleFilter}
                    onScheduleFilterChange={setScheduleFilter}
                    users={users}
                    ownerFilter={ownerFilter}
                    onOwnerFilterChange={setOwnerFilter}
                    tags={tags}
                    tagFilter={tagFilter}
                    onTagFilterChange={setTagFilter}
                    onClearFilters={() => {
                        setSearchTerm('');
                        setStatusFilterId(null);
                        setScheduleFilter(null);
                        setOwnerFilter(null);
                        setTagFilter(null);
                    }}
                    onOpenPicker={setBoardMenuAnchor}
                    showClosed={showClosed}
                    onToggleClosed={() => {
                        setShowClosed(!showClosed);
                        refreshCurrentBoard();
                    }}
                    view={view}
                    onViewChange={changeView}
                    onExport={exportLeads}
                    exporting={exporting}
                />

                <CrmBoardPicker
                    anchorEl={boardMenuAnchor}
                    summaries={summaries}
                    selectedBoardId={selectedBoardId}
                    board={board}
                    onClose={() => setBoardMenuAnchor(null)}
                    onSelect={setSelectedBoardId}
                    onEdit={setEditBoard}
                    onDelete={handleDeleteBoard}
                    onCreate={() => setCreatingBoardOpen(true)}
                    onToggleActive={handleToggleBoardActive}
                />

                {/* A lista continua montada ao trocar de quadro: desmontar perderia ordenação. */}
                {loading && (view === 'board' || !board) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {view === 'list' && board && (
                    <CrmLeadList
                        boardId={board.id}
                        columns={board.columns}
                        searchTerm={searchTerm}
                        statusFilterId={statusFilterId}
                        scheduleFilter={scheduleFilter}
                        ownerFilter={ownerFilter}
                        tagFilter={tagFilter}
                        showClosed={showClosed}
                        refresh={listRefresh}
                        containerRef={columnsRowRef}
                        height={columnsMaxHeight}
                        onOpenLead={(lead) => navigate(`/crm/lead/${lead.id}`)}
                        boardActive={board.active}
                        statuses={statuses}
                        users={users}
                        onChangeStatus={handleChangeLeadStatus}
                        onAssign={handleAssignLead}
                    />
                )}

                {view === 'board' && !loading && board && (
                    <CrmBoardColumns
                        board={board}
                        focusedLead={focusedLead}
                        focusedColumn={focusedColumn}
                        rowRef={columnsRowRef}
                        columnsMaxHeight={columnsMaxHeight}
                        scrollbarHeight={scrollbarHeight}
                        searchTerm={searchTerm}
                        statusFilterId={statusFilterId}
                        scheduleFilter={scheduleFilter}
                        ownerFilter={ownerFilter}
                        tagFilter={tagFilter}
                        statuses={statuses}
                        sensors={drag.sensors}
                        collisionDetection={drag.collisionDetection}
                        activeLead={drag.activeLead}
                        activeColumn={drag.activeColumn}
                        onDragStart={drag.handleDragStart}
                        onDragOver={drag.handleDragOver}
                        onDragEnd={drag.handleDragEnd}
                        onDragCancel={drag.handleDragCancel}
                        preview={drag.preview}
                        // O lead tem tela própria: o modal não comportava histórico nem ações com desfecho.
                        onOpenLead={(lead) => navigate(`/crm/lead/${lead.id}`)}
                        onChangeLeadStatus={handleChangeLeadStatus}
                        users={users}
                        onAssignLead={handleAssignLead}
                        onOpenSchedule={(lead) => navigate(`/crm/lead/${lead.id}`)}
                        onAddLead={setAddLeadColumnId}
                        onEditColumn={setEditColumn}
                        onDeleteColumn={handleDeleteColumn}
                        onSetEntryColumn={handleSetEntryColumn}
                        onAddColumn={() => setAddingColumn(true)}
                        canMoveToBoard={canMoveToBoard}
                    />
                )}

                {!loading && !board && summaries.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography sx={{ color: cc.textSecondary }}>{t('crmKanban.nenhumQuadro')}</Typography>
                    </Box>
                )}
            </Box>


            <CrmAddLeadModal
                open={Boolean(addLeadColumnId)}
                columnId={addLeadColumnId ?? ''}
                statuses={statuses}
                onClose={() => setAddLeadColumnId(null)}
                onCreated={refreshCurrentBoard}
            />

            <CrmColumnModal
                open={Boolean(editColumn) || addingColumn}
                boardId={selectedBoardId}
                columnCount={board?.columns.length ?? 0}
                column={editColumn}
                onClose={() => {
                    setEditColumn(null);
                    setAddingColumn(false);
                }}
                onSaved={refreshCurrentBoard}
            />

            <CrmWonValueModal
                open={dropped?.action === 'won'}
                leadName={dropped?.lead.contactName ?? ''}
                estimated={dropped?.lead.value}
                onCancel={() => setDropped(null)}
                onConfirm={(closedValue) => dropped && markOutcome(dropped.lead, ELeadOutcome.Won, { closedValue })}
            />

            <CrmLossReasonModal
                open={dropped?.action === 'lost'}
                leadName={dropped?.lead.contactName ?? ''}
                leadId={dropped?.lead.chatwootConversationId ? dropped.lead.id : null}
                onCancel={() => setDropped(null)}
                onConfirm={(lossReasonId, lossNote) =>
                    dropped && markOutcome(dropped.lead, ELeadOutcome.Lost, { lossReasonId, lossNote })}
            />

            <CrmMoveBoardModal
                lead={dropped?.action === 'move' ? dropped.lead : null}
                currentBoardId={selectedBoardId}
                summaries={summaries}
                onCancel={() => setDropped(null)}
                onMoved={async (boardName) => {
                    setDropped(null);
                    showAlert(t('crmKanban.movidoPara', { quadro: boardName }), 'success', 2500);
                    await refreshCurrentBoard();
                    await refetchBoards();
                }}
            />

            <CrmBoardModal
                open={creatingBoardOpen || Boolean(editBoard)}
                edit={editBoard}
                onClose={() => {
                    setCreatingBoardOpen(false);
                    setEditBoard(null);
                }}
                onCreated={refreshCurrentBoard}
                refetchBoards={refetchBoards}
                setSelectedBoardId={setSelectedBoardId}
            />
        </DashboardLayout>
    );
};
